/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kCoreTime — server-safe time and period classes.
 *
 * Exports:
 *   kKeyValueCore   — generic key/value pair
 *   kWorkPeriodCore — duration stored as minutes, with conversion helpers
 *   kTimeCore       — rich date/time value type (no moment.js dependency)
 *
 * No browser APIs. Safe to import in Node.js, Azure Functions, or the browser.
 */

import {
    getCoreUtils, nz, n0, kNotNullOrUndefined, roundToTwo, serverLocale,
} from "./kCoreHandler";

// ── kKeyValueCore ─────────────────────────────────────────────────────────────

export class kKeyValueCore {
    public key = "";
    public value: any;
}

// ── kWorkPeriodCore ──────────────────────────────────────────────────────────

/** A duration stored as whole minutes, with helpers for display and arithmetic. */
export class kWorkPeriodCore {
    public Minutes = 0;

    /** Whole hours component. */
    public Hours = (): number => {
        if (getCoreUtils().kErrorMode) { return 0; }
        try {
            return Math.floor(this.Minutes / 60);
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kWorkPeriod.Hours"); return 0; }
    };

    /** Total hours as a decimal rounded to two places (e.g. 1.5 for 90 min). */
    public HoursDec = (): number => {
        if (getCoreUtils().kErrorMode) { return 0; }
        try {
            return roundToTwo(this.Minutes / 60);
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kWorkPeriod.HoursDec"); return 0; }
    };

    /** Returns a zero-padded "HH:MM" string (e.g. "01:30" for 90 min). */
    public HoursAndMinutes = (): string => {
        if (getCoreUtils().kErrorMode) { return ""; }
        try {
            const H: number = this.Hours();
            const Remaining: number = this.Minutes - H * 60;
            const Prefix: string = H < 10
                ? ("0" + H.toString()).slice(-2)
                : H.toString();
            return Prefix + ":" + ("0" + Remaining.toString()).slice(-2);
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kWorkPeriod.HoursAndMinutes"); return ""; }
    };
}

// ── kTimeCore ─────────────────────────────────────────────────────────────────

/**
 * Rich date/time value type.
 *
 * All fields default to 0 / IsValid = false.
 * Use SetToNow(), fromJSDate(), FromISODate() etc. to populate.
 *
 * Static DateFormat controls ToLocalShortDate output (default "yyyy-MM-dd").
 * Set kTimeCore.DateFormat = "dd/MM/yyyy" to match a regional setting.
 */
export class kTimeCore {
    public Year: number;
    public Month: number;
    public Day: number;
    public Hour: number;
    public Minute: number;
    public Second: number;
    public MilliSecond: number;
    public IsValid: boolean;

    constructor() {
        const Now: Date = new Date();
        this.Year = Now.getFullYear();
        this.Month = Now.getMonth() + 1;
        this.Day = Now.getDate();
        this.Hour = Now.getHours();
        this.Minute = Now.getMinutes();
        this.Second = Now.getSeconds();
        this.MilliSecond = Now.getMilliseconds();
        this.IsValid = true;
    }

    /** Regional date format used by ToLocalShortDate. Override once at app startup. */
    public static DateFormat = "yyyy-MM-dd";

    // ── Comparison ────────────────────────────────────────────────────────────

    /** True if this date/time is in the past. */
    public isBefore = (): boolean => {
        if (getCoreUtils().kErrorMode) { return false; }
        try {
            return this.ToJsDate() < new Date();
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.isBefore"); return false; }
    };

    /** True if the weekday is Saturday (6) or Sunday (0). */
    public isWeekend = (): boolean => {
        if (getCoreUtils().kErrorMode) { return false; }
        try {
            const Day: number = this.Weekday();
            return Day === 0 || Day === 6;
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.isWeekend"); return false; }
    };

    /** True if this date matches today's date (ignores time). */
    public IsToday = (): boolean => {
        if (getCoreUtils().kErrorMode) { return false; }
        try {
            const Today: Date = new Date();
            return this.Year === Today.getFullYear()
                && this.Month === Today.getMonth() + 1
                && this.Day === Today.getDate();
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.IsToday"); return false; }
    };

    /** Number of calendar days from this date to OtherDate (inclusive). */
    public DaysToOtherDate = (OtherDate: kTimeCore): number => {
        if (getCoreUtils().kErrorMode) { return 0; }
        try {
            const MsPerDay = 86400000;
            return Math.floor(
                (OtherDate.TreatAsUTC().getTime() - this.TreatAsUTC().getTime()) / MsPerDay
            ) + 1;
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.DaysToOtherDate"); return 0; }
    };

    // ── Mutation ──────────────────────────────────────────────────────────────

    /** Set time fields to midnight (00:00:00.000). */
    public SetTimeToBeginningOfDay = (): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            this.Hour = 0; this.Minute = 0; this.Second = 0; this.MilliSecond = 0;
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.SetTimeToBeginningOfDay"); }
    };

    /** Set time fields to end of day (23:59:59.099). */
    public SetTimeToEndOfDay = (): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            this.Hour = 23; this.Minute = 59; this.Second = 59; this.MilliSecond = 99;
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.SetTimeToEndOfDay"); }
    };

    /** Parse "HH:MM" or "H:MM" and set Hour and Minute accordingly. */
    public SetTimeFromString = (TimeString: string): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            const Parts: string[] = TimeString.split(":");
            this.Hour = parseInt(Parts[0]);
            this.Minute = parseInt(Parts[1]);
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.SetTimeFromString"); }
    };

    /** Move to Monday of the current week at midnight. */
    public SetTimeToBeginningOfWeek = (): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            this.SetTimeToBeginningOfDay();
            if (this.Weekday() === 0) {
                this.Add("day", -6);
            } else {
                this.Add("day", -this.Weekday() + 1);
            }
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.SetTimeToBeginningOfWeek"); }
    };

    /** Move to Sunday of the current week at end-of-day. */
    public SetTimeToEndOfWeek = (): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            this.SetTimeToBeginningOfWeek();
            this.SetTimeToEndOfDay();
            this.Add("day", 6);
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.SetTimeToEndOfWeek"); }
    };

    /** Set day to 1 and time to midnight. */
    public SetDayToFirstOfMonth = (): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            this.Day = 1;
            this.SetTimeToBeginningOfDay();
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.SetDayToFirstOfMonth"); }
    };

    /** Advance to the last day of the current month. */
    public SetDayToLastOfMonth = (): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            const StartMonth: number = this.Month;
            this.Day = 0;
            for (let Counter = 0; Counter <= 31; Counter++) {
                this.AddDays(1);
                if (this.Month !== StartMonth) { break; }
            }
            this.AddDays(-1);
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.SetDayToLastOfMonth"); }
    };

    /** Set all fields from the current system time. */
    public SetToNow = (): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            this.fromJSDate(new Date());
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.SetToNow"); }
    };

    /**
     * Add (or subtract) units to this date.
     * interval: "year" | "quarter" | "month" | "week" | "day" | "hour" | "minute" | "second"
     */
    public Add = (interval: string, units: number): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            const SafeUnits: number = n0(units);
            if (SafeUnits === 0) { return; }
            const Ret: Date = this.ToJsDate();
            const CheckRollover = (): void => {
                if (Ret.getDate() !== this.ToJsDate().getDate()) { Ret.setDate(0); }
            };
            switch (String(interval).toLowerCase()) {
                case "year":    Ret.setFullYear(Ret.getFullYear() + SafeUnits); CheckRollover(); break;
                case "quarter": Ret.setMonth(Ret.getMonth() + 3 * SafeUnits);  CheckRollover(); break;
                case "month":   Ret.setMonth(Ret.getMonth() + SafeUnits);       CheckRollover(); break;
                case "week":    Ret.setDate(Ret.getDate() + 7 * SafeUnits);    break;
                case "day":     Ret.setDate(Ret.getDate() + SafeUnits);         break;
                case "hour":    Ret.setTime(Ret.getTime() + SafeUnits * 3600000); break;
                case "minute":  Ret.setTime(Ret.getTime() + SafeUnits * 60000);   break;
                case "second":  Ret.setTime(Ret.getTime() + SafeUnits * 1000);    break;
            }
            this.fromJSDate(Ret);
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.Add"); }
    };

    /** Add DaysToAdd calendar days. Returns the new JS Date. */
    public AddDays = (DaysToAdd: number): Date | null => {
        if (getCoreUtils().kErrorMode) { return null; }
        try {
            this.Add("day", DaysToAdd);
            return this.ToJsDate();
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.AddDays"); return null; }
    };

    /** Add minutes. Returns the new JS Date. */
    public AddMinutes = (MinutesToAdd: number): Date | null => {
        if (getCoreUtils().kErrorMode) { return null; }
        try {
            this.Add("minute", MinutesToAdd);
            return this.ToJsDate();
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.AddMinutes"); return null; }
    };

    /** Subtract minutes. Returns the new JS Date. */
    public SubractMinutes = (MinutesToSubtract: number): Date | null => {
        if (getCoreUtils().kErrorMode) { return null; }
        try {
            this.Add("minute", -MinutesToSubtract);
            return this.ToJsDate();
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.SubractMinutes"); return null; }
    };

    /** Subtract calendar days. Returns the new JS Date. */
    public SubractDays = (DaysToSubtract: number): Date | null => {
        if (getCoreUtils().kErrorMode) { return null; }
        try {
            this.Add("day", -DaysToSubtract);
            return this.ToJsDate();
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.SubractDays"); return null; }
    };

    // ── Conversion to JS Date ─────────────────────────────────────────────────

    /** Convert to a plain JS Date (local time). Day=0 is treated as day 1. */
    public ToJsDate = (): Date => {
        const DayOfMonth: number = this.Day === 0 ? 1 : this.Day;
        return new Date(
            this.Year, this.Month - 1, DayOfMonth,
            this.Hour, this.Minute, this.Second, this.MilliSecond
        );
    };

    /** Returns ToJsDate() shifted to UTC (timezone offset removed). */
    public TreatAsUTC = (): Date => {
        const Result: Date = this.ToJsDate();
        Result.setMinutes(Result.getMinutes() - Result.getTimezoneOffset());
        return Result;
    };

    // ── Population from JS Date / strings ─────────────────────────────────────

    /** Populate all fields from a JS Date object. */
    public fromJSDate = (NewDate: Date): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            if (kNotNullOrUndefined(NewDate)) {
                this.Year = NewDate.getFullYear();
                this.Month = NewDate.getMonth() + 1;
                this.Day = NewDate.getDate();
                this.Hour = NewDate.getHours();
                this.Minute = NewDate.getMinutes();
                this.Second = NewDate.getSeconds();
                this.MilliSecond = NewDate.getMilliseconds();
                this.IsValid = true;
            }
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.fromJSDate"); }
    };

    /** Alias for FromISODate — accepts "yyyy-MM-dd" strings. */
    public fromISO = (IsoStringDate: string): void => {
        this.FromISODate(IsoStringDate);
    };

    /** Parse an ISO date string "yyyy-MM-dd" (or "yyyy-MM-dd HH:mm…"). */
    public FromISODate = (IsoStringDate: string): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            if (IsoStringDate !== "") {
                const Parts: string[] = IsoStringDate.split(" ")[0].split("-");
                this.Year = parseInt(Parts[0]);
                this.Month = parseInt(Parts[1]);
                this.Day = parseInt(Parts[2]);
                this.IsValid = true;
            } else {
                this.Year = 0; this.Month = 0; this.Day = 0; this.IsValid = false;
            }
            this.Hour = 0; this.Minute = 0; this.Second = 0; this.MilliSecond = 0;
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.FromISODate"); }
    };

    /** Parse "yyyy-MM-dd HH:mm" (the format produced by ToISODateTime). */
    public FromISODateTime = (IsoStringDate: string): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            if (IsoStringDate !== "") {
                const MainParts: string[] = IsoStringDate.split(" ");
                this.FromISODate(MainParts[0]);
                const Parts: string[] = MainParts[1].split(":");
                this.Hour = parseInt(Parts[0]);
                this.Minute = parseInt(Parts[1]);
                this.Second = 0; this.MilliSecond = 0;
                this.IsValid = true;
            } else {
                this.IsValid = false;
                this.Year = 0; this.Month = 0;
                this.Hour = 0; this.Minute = 0; this.Second = 0; this.MilliSecond = 0;
            }
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.FromISODateTime"); }
    };

    /** Restore from a plain JSON object (e.g. as stored by ToJSONObj on kObject). */
    public FromJSON = (JSONObj: any): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            this.Year = JSONObj.Year; this.Month = JSONObj.Month; this.Day = JSONObj.Day;
            this.Hour = JSONObj.Hour; this.Minute = JSONObj.Minute;
            this.Second = JSONObj.Second; this.MilliSecond = JSONObj.MilliSecond;
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.FromJSON"); }
    };

    /** Parse a SharePoint REST date string ("2024-01-15T10:30:00Z"). */
    public FromSPDate = (SPDate: string, AllDay?: boolean): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            if (!SPDate) { this.IsValid = false; return; }
            const Normalised: string = AllDay === true
                ? SPDate.replace("T", " ").replace("Z", "")
                : SPDate;
            const NewDate: Date = new Date(Normalised);
            if (kNotNullOrUndefined(NewDate)) {
                this.fromJSDate(NewDate);
            } else {
                this.IsValid = false;
            }
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.FromSPDate"); }
    };

    /** Parse an SPFx date token ("Date;#2024-01-15T10:30:00Z"). */
    public FromSPFXDate = (SPFXDate: string): void => {
        if (getCoreUtils().kErrorMode) { return; }
        try {
            if (SPFXDate) {
                const Parts: string[] = SPFXDate.split(";#");
                if (Parts.length === 2) {
                    const NewDate: Date = new Date(Parts[1]);
                    if (kNotNullOrUndefined(NewDate)) {
                        this.fromJSDate(NewDate);
                    } else {
                        this.IsValid = false;
                    }
                }
            } else {
                this.IsValid = false;
            }
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.FromSPFXDate"); }
    };

    // ── ISO formatting ────────────────────────────────────────────────────────

    /** "yyyy-MM-dd" — empty string when year ≤ 1971. */
    public ToISODate = (): string => {
        if (getCoreUtils().kErrorMode) { return ""; }
        try {
            if (this.Year > 1971) {
                return this.Year
                    + "-" + ("0" + this.Month.toString()).slice(-2)
                    + "-" + ("0" + this.Day.toString()).slice(-2);
            }
            return "";
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.ToISODate"); return ""; }
    };

    /** "HH:mm" substring of ToISODateTime. */
    public ToISOTime = (): string => {
        if (getCoreUtils().kErrorMode) { return ""; }
        try {
            return this.Year > 1971 ? this.ToISODateTime().substring(11, 16) : "";
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.ToISOTime"); return ""; }
    };

    /** "yyyy-MM-dd HH:mm" */
    public ToISODateTime = (): string => {
        if (getCoreUtils().kErrorMode) { return ""; }
        try {
            return this.ToISODate()
                + " " + ("0" + this.Hour.toString()).slice(-2)
                + ":" + ("0" + this.Minute.toString()).slice(-2);
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.ToISODateTime"); return ""; }
    };

    /** "yyyy-MM-dd HH:mm ss" (adds seconds). */
    public ToISODateTimeSecs = (): string => {
        if (getCoreUtils().kErrorMode) { return ""; }
        try {
            return this.ToISODateTime()
                + " " + ("0" + this.Second.toString()).slice(-2);
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.ToISODateTimeSecs"); return ""; }
    };

    // ── Locale-aware formatting ───────────────────────────────────────────────

    /**
     * Formats the date using kTime.DateFormat (default "yyyy-MM-dd").
     * Set kTime.DateFormat = "dd/MM/yyyy" once at startup for regional formatting.
     */
    public ToLocalShortDate = (): string => {
        if (getCoreUtils().kErrorMode) { return ""; }
        try {
            if (this.Year <= 1970) { return ""; }
            let Fmt: string = kTimeCore.DateFormat.toLowerCase();
            Fmt = Fmt.replace("yyyy", this.Year.toString());
            Fmt = Fmt.replace("mm", ("0" + this.Month.toString()).slice(-2));
            Fmt = Fmt.replace("m", this.Month.toString());
            Fmt = Fmt.replace("dd", ("0" + this.Day.toString()).slice(-2));
            Fmt = Fmt.replace("d", this.Day.toString());
            return Fmt;
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.ToLocalShortDate"); return ""; }
    };

    /** "dd/MM/yyyy HH:mm" (or local format) + locale time string. */
    public ToLocalShortDateAndTime = (): string => {
        if (getCoreUtils().kErrorMode) { return ""; }
        try {
            if (this.Year <= 1969) { return ""; }
            return this.ToLocalShortDate() + " "
                + this.ToJsDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.ToLocalShortDateAndTime"); return ""; }
    };

    /** "HH:mm" from the underlying JS Date's toTimeString(). */
    public To2DigitTime = (): string => {
        if (getCoreUtils().kErrorMode) { return ""; }
        try {
            return this.Year > 1969 ? this.ToJsDate().toTimeString().substring(0, 5) : "";
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.To2DigitTime"); return ""; }
    };

    /** Full weekday name in the system locale (e.g. "Monday"). */
    public WeekdayName = (): string => {
        if (getCoreUtils().kErrorMode) { return ""; }
        try {
            if (!this.IsValid) { return ""; }
            return this.ToJsDate().toLocaleDateString(serverLocale(), { weekday: "long" });
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.WeekdayName"); return ""; }
    };

    /** Two-letter weekday abbreviation with first letter capitalised (e.g. "Mo"). */
    public WeekDayName2 = (): string => {
        if (getCoreUtils().kErrorMode) { return ""; }
        try {
            if (!this.IsValid) { return ""; }
            const Short: string = this.ToJsDate()
                .toLocaleDateString(serverLocale(), { weekday: "short" });
            return Short.substring(0, 1).toUpperCase() + Short.substring(1, 2);
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.WeekDayName2"); return ""; }
    };

    /** Full month name in the system locale (e.g. "January"). */
    public MonthName = (): string => {
        if (getCoreUtils().kErrorMode) { return ""; }
        try {
            return this.ToJsDate().toLocaleDateString(serverLocale(), { month: "long" });
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.MonthName"); return ""; }
    };

    // ── Calendar helpers ──────────────────────────────────────────────────────

    /** 0 = Sunday … 6 = Saturday (matches JS Date.getDay). */
    public Weekday = (): number => {
        if (getCoreUtils().kErrorMode) { return -1; }
        try {
            return this.ToJsDate().getDay();
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.Weekday"); return -1; }
    };

    /** ISO 8601 week number (1-53). */
    public WeekNumber = (): number => {
        if (getCoreUtils().kErrorMode) { return 0; }
        try {
            const D: Date = this.ToJsDate();
            D.setHours(0, 0, 0, 0);
            D.setDate(D.getDate() + 3 - ((D.getDay() + 6) % 7));
            const Week1: Date = new Date(D.getFullYear(), 0, 4);
            return 1 + Math.round(
                ((D.getTime() - Week1.getTime()) / 86400000
                    - 3 + ((Week1.getDay() + 6) % 7)) / 7
            );
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.WeekNumber"); return 0; }
    };

    /** yyyyWW integer useful for grouping by year+week (e.g. 202403). */
    public YearWeekNumber = (): number => {
        if (getCoreUtils().kErrorMode) { return 0; }
        try {
            return parseInt(this.Year.toString() + this.WeekNumber().toString());
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.YearWeekNumber"); return 0; }
    };

    // ── "From now" helpers ────────────────────────────────────────────────────

    /** Milliseconds since this point in time (positive = past, negative = future). */
    public MSecsFromNow = (): number => {
        if (getCoreUtils().kErrorMode) { return 0; }
        try {
            return Math.round(new Date().getTime() - this.ToJsDate().getTime());
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.MSecsFromNow"); return 0; }
    };

    /** Whole seconds since this point. */
    public SecondsFromNow = (): number => {
        if (getCoreUtils().kErrorMode) { return 0; }
        try {
            return Math.round(
                Math.round(new Date().getTime() / 1000)
                - this.ToJsDate().getTime() / 1000
            );
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.SecondsFromNow"); return 0; }
    };

    /** Alias for SecondsFromNow (rounded from milliseconds). */
    public SecsFromNow = (): number => {
        if (getCoreUtils().kErrorMode) { return 0; }
        try {
            return Math.round(this.MSecsFromNow() / 1000);
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.SecsFromNow"); return 0; }
    };

    /** Whole minutes since this point. */
    public MinutesFromNow = (): number => {
        if (getCoreUtils().kErrorMode) { return 0; }
        try {
            return Math.round(this.SecondsFromNow() / 60);
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.MinutesFromNow"); return 0; }
    };

    // ── Clone ─────────────────────────────────────────────────────────────────

    /** Return a deep copy of this kTimeCore. */
    public clone = (): kTimeCore => {
        if (getCoreUtils().kErrorMode) { return new kTimeCore(); }
        try {
            const T: kTimeCore = new kTimeCore();
            T.Year = this.Year; T.Month = this.Month; T.Day = this.Day;
            T.Hour = this.Hour; T.Minute = this.Minute; T.Second = this.Second;
            T.MilliSecond = this.MilliSecond; T.IsValid = this.IsValid;
            return T;
        } catch (e) { getCoreUtils().kGlobalErrorHandler(e, "kTime.clone"); return new kTimeCore(); }
    };
}
